import React, { useCallback, useEffect, useRef, useState } from "react";
import "./SpatialVideoPlayer.css";

export type SpatialProjectionMode =
  | "360° Spatial"
  | "180° VR Dome"
  | "Flat 2D"
  | "VR Stereo Split";

export const SPATIAL_PROJECTION_MODES: readonly SpatialProjectionMode[] = [
  "360° Spatial",
  "180° VR Dome",
  "Flat 2D",
  "VR Stereo Split",
];

export const YAW_LIMIT = 180;
export const PITCH_LIMIT = 85;
export const FOV_MIN = 60;
export const FOV_MAX = 110;
export const DEFAULT_FOV = 90;

export type SpatialViewState = {
  yaw: number;
  pitch: number;
  fov: number;
};

export const DEFAULT_VIEW: SpatialViewState = { yaw: 0, pitch: 0, fov: DEFAULT_FOV };

export type SpatialVideoPlayerProps = {
  src: string;
  /** Initial projection; defaults to 360° Spatial. */
  defaultMode?: SpatialProjectionMode;
  title?: string;
  className?: string;
  onModeChange?: (mode: SpatialProjectionMode) => void;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

/** Picks the projection that best matches a catalog format string ("360° Video", "180° VR", …). */
export function projectionModeForFormat(format: string | undefined | null): SpatialProjectionMode {
  const value = (format ?? "").toLowerCase();
  if (/180/.test(value)) return "180° VR Dome";
  if (/stereo|sbs|side[- ]by[- ]side|3d/.test(value)) return "VR Stereo Split";
  if (/360|spatial|vr|immersive/.test(value)) return "360° Spatial";
  return "Flat 2D";
}

/** Degrees moved per pixel dragged, scaled by the current field of view. */
function dragSensitivity(fov: number): number {
  return fov / 600;
}

function formatDegrees(value: number): string {
  return `${Math.round(value)}°`;
}

/** Wraps yaw into the -180..180 range so the viewer can spin continuously in 360° mode. */
function wrapYaw(yaw: number): number {
  let next = yaw;
  while (next > YAW_LIMIT) next -= 360;
  while (next < -YAW_LIMIT) next += 360;
  return next;
}

export function SpatialVideoPlayer({
  src,
  defaultMode = "360° Spatial",
  title,
  className,
  onModeChange,
}: SpatialVideoPlayerProps) {
  const [mode, setMode] = useState<SpatialProjectionMode>(defaultMode);
  const [view, setView] = useState<SpatialViewState>(DEFAULT_VIEW);
  const [dragging, setDragging] = useState(false);
  const dragOrigin = useRef<{ x: number; y: number; yaw: number; pitch: number } | null>(null);

  useEffect(() => {
    setMode(defaultMode);
  }, [defaultMode]);

  const isFlat = mode === "Flat 2D";
  const isStereo = mode === "VR Stereo Split";
  const isDome = mode === "180° VR Dome";
  const yawLimit = isDome ? 90 : YAW_LIMIT;

  const selectMode = (next: SpatialProjectionMode) => {
    setMode(next);
    setView(DEFAULT_VIEW);
    onModeChange?.(next);
  };

  const resetView = () => setView(DEFAULT_VIEW);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (isFlat) return;
    if (event.target instanceof HTMLVideoElement) return;
    dragOrigin.current = { x: event.clientX, y: event.clientY, yaw: view.yaw, pitch: view.pitch };
    setDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const origin = dragOrigin.current;
    if (!origin) return;
    const sensitivity = dragSensitivity(view.fov);
    const dx = (event.clientX - origin.x) * sensitivity * 2;
    const dy = (event.clientY - origin.y) * sensitivity * 2;
    setView((current) => ({
      ...current,
      yaw: isDome ? clamp(origin.yaw - dx, -yawLimit, yawLimit) : wrapYaw(origin.yaw - dx),
      pitch: clamp(origin.pitch + dy, -PITCH_LIMIT, PITCH_LIMIT),
    }));
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragOrigin.current) return;
    dragOrigin.current = null;
    setDragging(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const handleWheel = useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      if (isFlat) return;
      setView((current) => ({
        ...current,
        fov: clamp(current.fov + Math.sign(event.deltaY) * 4, FOV_MIN, FOV_MAX),
      }));
    },
    [isFlat]
  );

  const handleFovChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fov = clamp(Number(event.target.value), FOV_MIN, FOV_MAX);
    setView((current) => ({ ...current, fov }));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (isFlat) return;
    const step = 5;
    const turn = (delta: number) =>
      isDome ? clamp(view.yaw + delta, -yawLimit, yawLimit) : wrapYaw(view.yaw + delta);
    let next: Partial<SpatialViewState>;
    switch (event.key) {
      case "ArrowLeft": next = { yaw: turn(-step) }; break;
      case "ArrowRight": next = { yaw: turn(step) }; break;
      case "ArrowUp": next = { pitch: clamp(view.pitch + step, -PITCH_LIMIT, PITCH_LIMIT) }; break;
      case "ArrowDown": next = { pitch: clamp(view.pitch - step, -PITCH_LIMIT, PITCH_LIMIT) }; break;
      case "+": case "=": next = { fov: clamp(view.fov - step, FOV_MIN, FOV_MAX) }; break;
      case "-": case "_": next = { fov: clamp(view.fov + step, FOV_MIN, FOV_MAX) }; break;
      case "r": case "R": next = DEFAULT_VIEW; break;
      default: return;
    }
    event.preventDefault();
    setView((current) => ({ ...current, ...next }));
  };

  // Map the look direction onto a CSS transform: FOV drives zoom, yaw/pitch pan the
  // equirectangular frame so dragging feels like looking around inside the sphere.
  const zoom = isFlat ? 1 : DEFAULT_FOV / view.fov + (isDome ? 0.35 : 0.6);
  const panX = isFlat ? 0 : (-view.yaw / yawLimit) * (isDome ? 22 : 38);
  const panY = isFlat ? 0 : (view.pitch / PITCH_LIMIT) * 30;
  const frameStyle: React.CSSProperties = {
    transform: `translate(${panX}%, ${panY}%) scale(${zoom.toFixed(3)})`,
    transition: dragging ? "none" : "transform 0.18s ease-out",
  };

  const viewportClass = [
    "spatial-player__viewport",
    isFlat ? "spatial-player__viewport--flat" : "spatial-player__viewport--immersive",
    isDome ? "spatial-player__viewport--dome" : "",
    isStereo ? "spatial-player__viewport--stereo" : "",
    dragging ? "spatial-player__viewport--dragging" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={["spatial-player", className].filter(Boolean).join(" ")}
      data-testid="spatial-video-player"
      data-mode={mode}
    >
      <div className="spatial-player__toolbar" role="toolbar" aria-label="Projection mode">
        {SPATIAL_PROJECTION_MODES.map((option) => (
          <button
            key={option}
            type="button"
            className={[
              "spatial-player__mode",
              option === mode ? "spatial-player__mode--active" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            aria-pressed={option === mode}
            onClick={() => selectMode(option)}
          >
            {option}
          </button>
        ))}
      </div>

      <div
        className={viewportClass}
        data-testid="spatial-viewport"
        role="application"
        aria-label={title ? `${title} spatial viewport` : "Spatial video viewport"}
        aria-roledescription={isFlat ? "flat video" : "drag to look around"}
        tabIndex={0}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerLeave={endDrag}
        onWheel={handleWheel}
        onKeyDown={handleKeyDown}
      >
        <div className="spatial-player__frame" data-testid="spatial-frame" style={frameStyle}>
          <video
            data-testid="inline-title-player"
            controls
            autoPlay
            muted
            playsInline
            src={src}
          />
        </div>
        {isStereo ? (
          <div className="spatial-player__stereo" aria-hidden data-testid="stereo-overlay">
            <div className="spatial-player__lens spatial-player__lens--left" data-testid="stereo-lens">
              <span className="spatial-player__lens-label">L</span>
            </div>
            <div className="spatial-player__lens spatial-player__lens--right" data-testid="stereo-lens">
              <span className="spatial-player__lens-label">R</span>
            </div>
          </div>
        ) : null}
        {isStereo ? (
          <span className="spatial-player__stereo-badge" data-testid="stereo-badge">
            Synchronized side-by-side stereo · L/R lenses
          </span>
        ) : null}
        {!isFlat ? (
          <div className="spatial-player__grid" aria-hidden />
        ) : null}
      </div>

      <div className="spatial-player__hud">
        <output className="spatial-player__readout" data-testid="spatial-readout" aria-live="polite">
          <span>
            <small>Yaw</small> {formatDegrees(view.yaw)}
          </span>
          <span>
            <small>Pitch</small> {formatDegrees(view.pitch)}
          </span>
          <span>
            <small>FOV</small> {formatDegrees(view.fov)}
          </span>
        </output>
        <label className="spatial-player__fov">
          <span className="spatial-player__sr-only">Field of view</span>
          <input
            type="range"
            min={FOV_MIN}
            max={FOV_MAX}
            step={1}
            value={view.fov}
            onChange={handleFovChange}
            disabled={isFlat}
            aria-label="Field of view"
          />
        </label>
        <button
          type="button"
          className="spatial-player__reset"
          onClick={resetView}
          disabled={isFlat}
        >
          Reset View
        </button>
      </div>
    </div>
  );
}

export default SpatialVideoPlayer;
